/**
 * Seafood country-of-origin display helper (mobile, "dumb display").
 * 海鲜产地展示工具（mobile，纯展示）。
 *
 * The backend is the sole authority on whether a product carries a
 * `seafoodOrigin` value (it only sends one for an Australian venue whose
 * product contains Seafood and has the origin set). This module does NO
 * country logic — it just turns an origin value into a localized word and
 * composes the per-tag label `Seafood • {origin}`. Unknown / empty origins
 * fall back to the bare `Seafood` text so dirty data never breaks the page.
 *
 * 后端是产地的唯一权威：只有「澳洲 venue + 含 Seafood + 已设值」时才下发
 * seafoodOrigin。本模块不做任何国家判断，只把产地值翻成本地化词并拼成
 * `Seafood • {产地}`。未知/为空 → 退回纯 `Seafood`，脏数据不崩页。
 */
import { t } from '@/utils/language'

// The containsTags string that represents seafood (matches backend convention).
// containsTags 数组里代表海鲜的字符串（与后端约定一致）。
export const SEAFOOD_TAG = 'Seafood'

// Map an origin enum value to its i18n key + English fallback.
// 产地枚举值 → i18n key + 英文回落。
const ORIGIN_TO_I18N = {
  Australian: { key: 'seafood_origin_australian', fallback: 'Australian' },
  Imported: { key: 'seafood_origin_imported', fallback: 'Imported' },
  'Mixed Origin': { key: 'seafood_origin_mixed', fallback: 'Mixed Origin' }
}

/**
 * Localize an origin enum value. Unknown / empty → '' (guard for dirty data).
 * 把产地枚举值本地化。未知/为空 → ''（脏数据守卫）。
 *
 * @param {string} origin - 'Australian' | 'Imported' | 'Mixed Origin'
 * @returns {string} localized word, or '' when not recognised
 */
export function localizedSeafoodOrigin(origin) {
  const entry = ORIGIN_TO_I18N[origin]
  if (!entry) return ''
  return t(entry.key, entry.fallback)
}

/**
 * Compose the seafood contains label: `Seafood • {localized origin}`.
 * Falls back to the bare tag when the origin is missing / unrecognised.
 * 拼海鲜 contains 文案：`Seafood • {本地化产地}`；无/未知产地时退回纯标签。
 *
 * @param {string} tag - the raw contains tag (expected 'Seafood')
 * @param {string} origin - the seafoodOrigin value (may be null / unknown)
 * @returns {string}
 */
export function seafoodContainsLabel(tag, origin) {
  const localized = localizedSeafoodOrigin(origin)
  if (!localized) return tag
  return `${tag} • ${localized}`
}

/**
 * Decorate one contains-tag for display. Only the seafood tag is ever
 * decorated; every other tag is returned unchanged.
 * 给单个 contains 标签做展示装饰：只装饰海鲜标签，其它标签原样返回。
 *
 * @param {string} tag - one entry from the containsTags array
 * @param {string} origin - the product's seafoodOrigin (may be null)
 * @returns {string} display text for that tag
 */
export function decorateContainsTag(tag, origin) {
  if (tag !== SEAFOOD_TAG) return tag
  return seafoodContainsLabel(tag, origin)
}
